import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

const W = 320;
const H = 160;
const PAD = { t: 12, r: 8, b: 28, l: 28 };

function buildPath(points, maxY) {
    if (!points.length) return "";
    const innerW = W - PAD.l - PAD.r;
    const innerH = H - PAD.t - PAD.b;
    const coords = points.map((p, i) => {
        const x = PAD.l + (i / Math.max(points.length - 1, 1)) * innerW;
        const y = PAD.t + innerH - (p.sessions / maxY) * innerH;
        return [x, y];
    });
    const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const area = `${line} L ${coords[coords.length - 1][0].toFixed(1)} ${PAD.t + innerH} L ${coords[0][0].toFixed(1)} ${PAD.t + innerH} Z`;
    return { line, area, coords };
}

export default function SessionsAreaChart({ series = [] }) {
    const { line, area, coords, maxY, yTicks } = useMemo(() => {
        const pts = Array.isArray(series) ? series : [];
        const max = Math.max(1, ...pts.map((p) => Number(p.sessions) || 0));
        const yTicksLocal = [0, Math.ceil(max / 2), max];
        if (!pts.length) return { line: "", area: "", coords: [], maxY: max, yTicks: yTicksLocal };
        const built = buildPath(pts, max);
        return { ...built, maxY: max, yTicks: yTicksLocal };
    }, [series]);

    if (!series?.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No session history yet for this period.
            </Typography>
        );
    }

    const firstLabel = series[0]?.label;
    const lastLabel = series[series.length - 1]?.label;

    return (
        <Box sx={{ width: "100%" }}>
            <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: "100%", height: 160, display: "block" }}>
                {yTicks.map((tick) => {
                    const y = PAD.t + (H - PAD.t - PAD.b) * (1 - tick / maxY);
                    return (
                        <g key={tick}>
                            <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="currentColor" strokeOpacity={0.08} />
                            <text x={PAD.l - 6} y={y + 3} textAnchor="end" fontSize="9" fill="currentColor" opacity={0.45}>
                                {tick}
                            </text>
                        </g>
                    );
                })}
                <defs>
                    <linearGradient id="sessionsAreaFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="currentColor" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="currentColor" stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <path d={area} fill="url(#sessionsAreaFill)" style={{ color: "var(--mui-palette-primary-main, #58A6FF)" }} />
                <path
                    d={line}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    style={{ color: "var(--mui-palette-primary-main, #58A6FF)" }}
                />
                {coords.map(([x, y], i) => (
                    <circle
                        key={`${series[i]?.date}-${i}`}
                        cx={x}
                        cy={y}
                        r={3}
                        fill="currentColor"
                        style={{ color: "var(--mui-palette-primary-main, #58A6FF)" }}
                    />
                ))}
                <text x={PAD.l} y={H - 6} fontSize="9" fill="currentColor" opacity={0.5}>
                    {firstLabel}
                </text>
                <text x={W - PAD.r} y={H - 6} fontSize="9" fill="currentColor" opacity={0.5} textAnchor="end">
                    {lastLabel}
                </text>
            </Box>
        </Box>
    );
}
